import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { products } from "@/mocks/products";
import { images } from "@/config/images";
import { site } from "@/config/site";

const imageMap: Record<string, string> = {
  product1: images.product1,
  product2: images.product2,
  product3: images.product3,
  product4: images.product4,
};

export default function Products() {
  const recommended = products.filter((p) => p.category === "おすすめ");
  const pet = products.filter((p) => p.category === "ペット用");

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main className="pt-24 md:pt-28 pb-16">
        {/* ページタイトル */}
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-14 text-center">
            <p className="inline-flex items-center gap-2 text-xs md:text-sm font-bold text-primary-700 bg-primary-100 rounded-full px-4 py-1.5 mb-4">
              <i className="ri-store-2-line"></i>
              商品・サービス
            </p>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
              猪肉を、全国へ
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
              佐世保市の自然が育んだジビエ。こだわりの猪肉を全国へお届けします。
            </p>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 md:px-6 mt-10">
          {/* おすすめ商品 */}
          <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-4 flex items-center gap-2">
            <i className="ri-star-line text-primary-500"></i>
            おすすめ商品
          </h2>
          <div data-product-shop className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {recommended.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          {/* ペット用商品 */}
          <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mt-12 mb-4 flex items-center gap-2">
            <i className="ri-ghost-line text-accent-500"></i>
            ペット用商品
          </h2>
          <div data-product-shop className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {pet.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          {/* LINE案内 */}
          <div className="mt-12 bg-secondary-100 rounded-lg border border-secondary-200 p-6 md:p-8 text-center">
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              その他の商品も随時配信いたします。<br className="hidden sm:inline" />
              新商品や入荷情報をお知らせしますので、ぜひ公式LINEをご登録ください。
            </p>
            <a
              href={site.lineUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 mt-5 bg-[#06C755] hover:bg-[#05b34d] text-white text-sm font-bold px-8 py-3.5 rounded-md whitespace-nowrap transition-colors"
            >
              <i className="ri-chat-1-line"></i>
              公式LINEに登録する
            </a>
            <p className="mt-2 text-xs text-foreground-500">
              ※LINEは新商品・入荷情報のお知らせやお問い合わせ用です。商品購入はWebサイト内から行えます。
            </p>
          </div>

          {/* 購入導線 */}
          <div className="mt-8 bg-primary-500 rounded-lg p-8 md:p-10 text-center">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-background-50">
              商品をご購入いただけます
            </h2>
            <p className="mt-3 text-sm md:text-base text-background-50/90 max-w-2xl mx-auto">
              商品をお選びいただき、必要事項をご入力ください。
            </p>
            <Link
              to="/purchase"
              className="inline-flex items-center justify-center gap-2 mt-6 bg-background-50 text-primary-700 text-base font-bold px-8 py-3.5 rounded-md whitespace-nowrap transition-colors hover:bg-background-100"
            >
              <i className="ri-shopping-bag-line"></i>
              購入ページへ
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function ProductCard({ product }: { product: (typeof products)[number] }) {
  const isSoldOut = "soldOut" in product && product.soldOut;

  return (
    <article className="bg-background-50 rounded-lg border border-background-200 overflow-hidden flex flex-col">
      <div className="relative w-full aspect-[4/3] bg-background-50 flex items-center justify-center overflow-hidden">
        <img
          src={imageMap[product.imageKey]}
          alt={`${product.name} 商品写真`}
          className="w-full h-full object-contain"
        />
        {isSoldOut && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="inline-flex items-center gap-1.5 bg-foreground-800 text-background-50 text-sm font-bold px-5 py-2 rounded-full">
              <i className="ri-close-circle-line"></i>
              売り切れ
            </span>
          </div>
        )}
      </div>
      <div className="p-4 md:p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-base md:text-lg font-heading font-bold text-foreground-950">
            {product.name}
          </h3>
          <span className="shrink-0 inline-flex items-center rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium px-3 py-1 whitespace-nowrap">
            {product.category}
          </span>
        </div>
        <p className="mt-2 text-2xl font-heading font-bold text-primary-600">
          {product.priceLabel}
        </p>
        <p className="mt-1 text-xs md:text-sm text-foreground-500">{product.weight}</p>
        <p className="mt-3 text-sm text-foreground-700 leading-relaxed">
          {product.description}
        </p>
        {product.note && (
          <p className="mt-3 text-xs text-foreground-500 leading-relaxed bg-background-100 rounded-md p-3 border border-background-200">
            <i className="ri-information-line mr-1 text-accent-600"></i>
            {product.note}
          </p>
        )}
        <div className="mt-4 pt-3 border-t border-background-200">
          {isSoldOut ? (
            <button
              disabled
              className="inline-flex items-center justify-center gap-2 w-full bg-background-300 text-foreground-400 text-sm font-bold px-5 py-3 rounded-md whitespace-nowrap cursor-not-allowed"
            >
              <i className="ri-close-circle-line"></i>
              売り切れ
            </button>
          ) : (
            <Link
              to="/purchase"
              className="inline-flex items-center justify-center gap-2 w-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-bold px-5 py-3 rounded-md whitespace-nowrap transition-colors"
            >
              <i className="ri-shopping-bag-line"></i>
              購入する
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}